import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { formattedDevicesConfigs, mergeFormattedEntries, ensureCommunicationDiagnostic } from "../helpers/utils";
import { fetchDevicesConfigs } from "./devicesConfigsDuck";
import { upsertAlarmsForMap } from "./alarmsDuck";
import { RefreshSignalR } from "../actions/apiActions";
import config from "config";

export const devicesDefaultState = {
  entries: {},
  formattedEntries: {},
  extraDiagnostics: {},
  dataTimeStamp: 0,
  rawSensorData: [],
  lastSocketBatch: null,
};

// Thunk functions
export const fetchEdgeDataSocket = createAsyncThunk("devices/fetchEdgeDataSocket", async (params, thunk) => {
  const { data, systemId, processAsRawSensorData } = params;
  if (processAsRawSensorData === true) {
    return {
      systemId,
      data,
      processAsRawSensorData,
    };
  }

  const devicesConfigs = thunk.getState().config[systemId].devicesConfigs.devicesConfigs;
  const dataTimeStamp = data && data[0].ValueTimeStamp ? data[0].ValueTimeStamp : undefined;
  const samplesConfigs = thunk.getState().config[systemId].devicesConfigs.samplesConfigs;

  return {
    systemId,
    data,
    devicesConfigs,
    dataTimeStamp,
    samplesConfigs,
  };
});

export const refreshSignalR = createAsyncThunk("system/refreshSignalR", async (data, thunk) => {
  // console.log("refreshSignalR data", data);
  const { hwId, SiteId, EdgeUnitId, url } = data;
  const config = data.payload;
  const param = {
    hwId,
    SiteId: SiteId ? SiteId : config.CUSTOMER_ID,
    EdgeUnitId: EdgeUnitId ? EdgeUnitId : config.DEVICE_ID,
  };
  const delay = data.delay;

  // Dealying refresh call to be sure that websockets have time to connect
  new Promise(() => {
    setTimeout(() => {
      RefreshSignalR.post(url ? url : config.STREAMER_URL, param);
    }, delay);
  });
});

const devicesSlice = createSlice({
  name: "devices",
  initialState: devicesDefaultState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevicesConfigs.fulfilled, (state, action) => {
        const { deviceConfigs } = action.payload.response;
        const formattedDeviceConfigs = formattedDevicesConfigs(ensureCommunicationDiagnostic(deviceConfigs.devices));
        // Seed formattedEntries with config defaults so the selector can generate the initial device list
        state.formattedEntries = formattedDeviceConfigs;
      })
      .addCase(upsertAlarmsForMap.fulfilled, (state, action) => {
        // Alarm data is stored in alarmsDuck; devicesList is now derived by selector
      })
      .addCase(fetchEdgeDataSocket.fulfilled, (state, action) => {
        const { data, devicesConfigs, dataTimeStamp, processAsRawSensorData } = action.payload;
        // console.log("fetchEdgeDataSocket fulfilled", action);

        if (processAsRawSensorData) {
          const newDataMap = new Map();

          // Set data to map, unique key is HwId_ValueName
          data.forEach((dataPoint) => {
            newDataMap.set(`${dataPoint.HwId}_${dataPoint.ValueName}`, dataPoint);
          });

          // Merge old data with new data, update existing items and remove them from map
          const mergedRawData = state.rawSensorData.map((item) => {
            const key = `${item.HwId}_${item.ValueName}`;
            if (newDataMap.has(key)) {
              const updated = { ...item, ...newDataMap.get(key) };
              newDataMap.delete(key); // Remove to avoid re-adding
              return updated;
            }
            return item;
          });

          // Add any new items that weren't in the old array
          const newItems = Array.from(newDataMap.values());
          state.rawSensorData = [...mergedRawData, ...newItems];
          return;
        }

        // data is an array of objects

        // Update state.entries directly with the new data
        // state.entries = {
        //   [data.ValueName]: data
        // };

        state.dataTimeStamp = dataTimeStamp;
        state.lastSocketBatch = data;

        state.formattedEntries = mergeFormattedEntries(data, state.formattedEntries, devicesConfigs);

        data.forEach((dataObject) => {
          // Check if device configuration exists for the current hwId and value name
          if (!devicesConfigs[dataObject.HwId]?.[dataObject.ValueName]) {
            const diagValue = isNaN(dataObject.Value) ? dataObject.Value : Number(dataObject.Value).toFixed(2);

            // Update of extraDiagnostics for the current hwId
            state.extraDiagnostics = {
              ...state.extraDiagnostics,
              [dataObject.HwId]: {
                // Spread previous values or initialize an empty object if undefined
                ...(state.extraDiagnostics[dataObject.HwId] || {}),
                [dataObject.ValueName]: {
                  displayName: dataObject.ValueName,
                  value: dataObject.ValueString ? `${dataObject.ValueString}  -  ${diagValue}` : diagValue,
                  unit: dataObject.Unit,
                  hwId: dataObject.HwId,
                },
              },
            };
          }
        });
      });
  },
});

export const { } = devicesSlice.actions;

export default devicesSlice.reducer;
